<template>
  <div>
    <b-card no-body class="mb-1">
      <b-card-body>
        <b-row class="align-items-center">
          <b-col cols="12" md="4" class="mb-1 mb-md-0">
            <b-form-radio-group
              v-model="russian"
              :options="russianOptions"
              buttons
              button-variant="outline-primary"
              size="sm"
            />
          </b-col>
          <b-col cols="6" md="2" class="mb-1 mb-md-0">
            <b-form-select v-model="decade" :options="decadeOptions" size="sm" />
          </b-col>
          <b-col cols="6" md="3" class="mb-1 mb-md-0">
            <b-form-select v-model="clusterId" :options="clusterSelectOptions" size="sm" />
          </b-col>
          <b-col cols="12" md="3" class="text-md-right">
            <b-badge variant="light-primary" class="font-medium-1">Осталось: {{ remaining }}</b-badge>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <b-overlay :show="isLoading" rounded="sm">
      <SimilarFeedRow
        v-for="item in items"
        :key="item.artist.id"
        :item="item"
        @saved="onRowSaved"
        @skipped="onRowSkipped"
        @candidates-loaded="onCandidatesLoaded"
      />
      <b-card v-if="!isLoading && !items.length" no-body>
        <b-card-body class="text-center text-muted">Всё размечено</b-card-body>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCard, BCardBody, BRow, BCol, BBadge, BOverlay, BFormRadioGroup, BFormSelect,
} from 'bootstrap-vue'
import { ref, watch, onUnmounted } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import musicStoreModule from '@/views/db-music/musicStoreModule'
import SimilarFeedRow from '@/views/db-music/SimilarFeedRow.vue'

const STORE_MODULE_NAME = 'similarFeed'
const PAGE_SIZE = 20
// Сколько последних пропущенных артистов передаём в exclude_ids
const MAX_SKIPPED_EXCLUDED = 200

export default {
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BBadge,
    BOverlay,
    BFormRadioGroup,
    BFormSelect,
    SimilarFeedRow,
  },
  setup() {
    const toast = useToast()

    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, musicStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    // 1 — русские, 0 — зарубежные, null — все
    const russian = ref(1)
    const decade = ref(null)
    const clusterId = ref(null)
    const items = ref([])
    const remaining = ref(0)
    const isLoading = ref(false)
    // Пропущенные в этой сессии артисты (сохранённые сервер и так не отдаёт)
    const skippedIds = ref([])
    // Поколение ленты: растёт при каждой перезагрузке (смене фильтра), устаревшие ответы игнорируем
    let feedGeneration = 0

    const russianOptions = [
      { text: 'Русские', value: 1 },
      { text: 'Зарубежные', value: 0 },
      { text: 'Все', value: null },
    ]
    const decadeOptions = [
      { text: 'Любое десятилетие', value: null },
      ...[1950, 1960, 1970, 1980, 1990, 2000, 2010, 2020].map(year => ({ text: `${year}-е`, value: year })),
    ]
    const clusterSelectOptions = ref([{ text: 'Любой кластер', value: null }])

    const showError = error => toast({
      component: ToastificationContent,
      props: {
        title: (error.response && error.response.data && error.response.data.message) || error.message || 'Ошибка загрузки',
        icon: 'AlertTriangleIcon',
        variant: 'danger',
      },
    })

    const buildParams = (limit, excluded) => {
      const params = { limit, exclude_ids: excluded }
      if (russian.value !== null) params.russian = russian.value
      if (decade.value !== null) params.decade = decade.value
      if (clusterId.value !== null) params.cluster_id = clusterId.value

      return params
    }

    const loadFeed = async () => {
      feedGeneration += 1
      const generation = feedGeneration
      isLoading.value = true
      skippedIds.value = []
      try {
        const response = await store.dispatch(`${STORE_MODULE_NAME}/fetchSimilarFeed`, buildParams(PAGE_SIZE, []))
        // Пока ждали, фильтр сменился — ответ относится к старой ленте
        if (generation !== feedGeneration) return
        items.value = response.data.items || []
        remaining.value = response.data.remaining || 0
      } catch (error) {
        if (generation === feedGeneration) showError(error)
      } finally {
        if (generation === feedGeneration) isLoading.value = false
      }
    }

    // Убираем строку и дозапрашиваем одного артиста вниз списка
    const removeRow = async artistId => {
      const generation = feedGeneration
      items.value = items.value.filter(item => item.artist.id !== artistId)
      remaining.value = Math.max(0, remaining.value - 1)

      // Исключаем только видимые строки (чтобы не получить дубль) и последние пропущенные:
      // сохранённые сервер не отдаёт сам, а полный список за сессию раздул бы URL
      const excluded = [...new Set([
        ...items.value.map(item => item.artist.id),
        ...skippedIds.value.slice(-MAX_SKIPPED_EXCLUDED),
      ])]

      try {
        const response = await store.dispatch(`${STORE_MODULE_NAME}/fetchSimilarFeed`, buildParams(1, excluded))
        if (generation !== feedGeneration) return
        const newItems = (response.data.items || []).filter(
          newItem => !items.value.some(item => item.artist.id === newItem.artist.id),
        )
        items.value = [...items.value, ...newItems]
      } catch (error) {
        if (generation === feedGeneration) showError(error)
      }
    }

    const onRowSkipped = artistId => {
      skippedIds.value = [...skippedIds.value, artistId].slice(-MAX_SKIPPED_EXCLUDED)
      removeRow(artistId)
    }

    // Обратные связи: сохранённый артист появился в похожих у этих артистов — переносим его
    // в «существующие» их видимых строк, чтобы повторный клик не дублировал связь
    const applyReverseLinks = (savedArtist, reverseLinkedIds) => {
      if (!savedArtist || !reverseLinkedIds || !reverseLinkedIds.length) return

      const savedTitle = String(savedArtist.title || '').toLowerCase()

      items.value.forEach(item => {
        if (!reverseLinkedIds.includes(item.artist.id)) return

        item.candidates = item.candidates.filter(candidate => candidate.id !== savedArtist.id)

        if (item.existing.some(row => row.similar_artist_id === savedArtist.id)) return

        // Свободная строка с тем же именем сервером привязана к артисту
        const freeRow = item.existing.find(
          row => row.similar_artist_id === null && String(row.title || '').toLowerCase() === savedTitle,
        )
        if (freeRow) {
          freeRow.similar_artist_id = savedArtist.id
          freeRow.track = savedArtist.track || null
          return
        }

        const maxRank = item.existing.reduce((max, row) => Math.max(max, row.rank || 0), 0)
        item.existing = [...item.existing, {
          similar_artist_id: savedArtist.id,
          title: savedArtist.title,
          rank: maxRank + 1,
          track: savedArtist.track || null,
        }]
      })
    }

    const onRowSaved = ({ artistId, reverseLinkedArtistIds }) => {
      const savedItem = items.value.find(item => item.artist.id === artistId)
      applyReverseLinks(savedItem && savedItem.artist, reverseLinkedArtistIds)
      removeRow(artistId)
    }

    // «Ещё N» в строке: дописываем новых кандидатов в конец (дубли отсекаем — на всякий случай)
    const onCandidatesLoaded = ({ artistId, candidates, hasMore }) => {
      const item = items.value.find(row => row.artist.id === artistId)
      if (!item) return

      const shownIds = item.candidates.map(candidate => candidate.id)
      item.candidates = [...item.candidates, ...candidates.filter(candidate => !shownIds.includes(candidate.id))]
      item.has_more_candidates = hasMore
    }

    store.dispatch(`${STORE_MODULE_NAME}/fetchClusters`)
      .then(response => {
        const clusters = response.data.data || []
        clusterSelectOptions.value = [
          { text: 'Любой кластер', value: null },
          ...clusters.map(cluster => ({ text: cluster.title, value: cluster.id })),
        ]
      })
      .catch(() => {})

    watch([russian, decade, clusterId], loadFeed)
    loadFeed()

    return {
      russian,
      decade,
      clusterId,
      items,
      remaining,
      isLoading,
      russianOptions,
      decadeOptions,
      clusterSelectOptions,
      onRowSaved,
      onRowSkipped,
      onCandidatesLoaded,
    }
  },
}
</script>
